// Safe Web Storage wrapper.
//
// Storage access throws — not just returns null — under a restrictive privacy
// mode, an MDM profile, or when the origin's quota is exhausted. A bare
// `localStorage.getItem()` inside a render body or a useState initializer
// therefore takes the whole app down to a blank screen. Every call site in
// src/ goes through these helpers instead.
//
// Contract: reads return the fallback on failure, writes report success as a
// boolean. Nothing here ever throws.
//
// In-memory fallback: when real storage is unavailable, values still live in a
// per-tab Map. Because this SPA never reloads between routes, a tech on a
// storage-disabled iPad keeps their active scan / session / name across
// navigation — state is only lost on a hard reload, not on every screen change.

const memory = new Map();

function warn(op, key, err) {
  console.warn(`storage: ${op}("${key}") failed — ${err && err.name ? err.name : err}`);
}

export function readLocal(key, fallback = null) {
  try {
    const v = window.localStorage.getItem(key);
    if (v !== null) return v;
  } catch (err) {
    warn('readLocal', key, err);
  }
  return memory.has(key) ? memory.get(key) : fallback;
}

export function writeLocal(key, value) {
  memory.set(key, String(value));
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (err) {
    warn('writeLocal', key, err);
    return false;
  }
}

export function removeLocal(key) {
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (err) {
    warn('removeLocal', key, err);
    return false;
  }
}

/** Read + JSON.parse. Returns fallback on a storage error OR malformed JSON. */
export function readLocalJson(key, fallback = null) {
  const raw = readLocal(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw);
  } catch (err) {
    warn('readLocalJson', key, err);
    return fallback;
  }
}

/** JSON.stringify + write. Returns false if the value can't be serialized. */
export function writeLocalJson(key, value) {
  let raw;
  try {
    raw = JSON.stringify(value);
  } catch (err) {
    warn('writeLocalJson', key, err);
    return false;
  }
  return writeLocal(key, raw);
}

// Session helpers keep a separate memory namespace so a sessionStorage failure
// (the admin PIN read runs on every API call) can't crash a request either.
const sessionMemory = new Map();

export function readSession(key, fallback = null) {
  try {
    const v = window.sessionStorage.getItem(key);
    if (v !== null) return v;
  } catch (err) {
    warn('readSession', key, err);
  }
  return sessionMemory.has(key) ? sessionMemory.get(key) : fallback;
}

export function writeSession(key, value) {
  sessionMemory.set(key, String(value));
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (err) {
    warn('writeSession', key, err);
    return false;
  }
}

export function removeSession(key) {
  sessionMemory.delete(key);
  try {
    window.sessionStorage.removeItem(key);
    return true;
  } catch (err) {
    warn('removeSession', key, err);
    return false;
  }
}

/** Shared storage keys, so a typo can't silently orphan a tech's saved state. */
export const KEYS = {
  techName: 'techName',
  // Signed-in Google account from Cloudflare Access (D-LAUNCH2). Empty on the
  // typed-name fallback.
  techEmail: 'techEmail',
  // + the email (or 'local' on the fallback): each tech sees the welcome once,
  // even on a shared iPad (D-LAUNCH1).
  welcomeSeenPrefix: 'welcomeSeen:',
  lastScan: 'lastScan',
  adminPin: 'adminPin',
  dyslexicFont: 'a11y:dyslexicFont',
  troubleshootSessionPrefix: 'troubleshootSession:',
};

// Same-tab notification that the tech name changed. The browser's native
// 'storage' event only fires in OTHER tabs, so a component that writes the name
// (Home's field, the Layout name gate) dispatches this to let the Layout gate
// re-read within the same tab.
export const TECH_NAME_EVENT = 'faultfinder:techname';
